import { usePumpRankingMarketCap } from '@hooks/use-pump-ranking';
import Loading from '@ui/loading';
import { formatDigitUnit } from '@utils/format-digit-unit';
import { type FC } from 'react';
import styles from './index.module.scss';
import Footer from '@components/footer';
import { useNavigate } from 'react-router-dom';
import Top from '@components/top';
import Lottie from 'lottie-react';
import bannerLeaderboardJSON from '@assets/lottie/pump/banner-leaderboard.json';
import { rankingIcons } from '@constants/icons';
import { timeDifference } from '@utils/time-difference';
import Avatar from '@components/avatar';
import { useWindowSize } from 'react-use';
import PumpItemMobile from '@components/pump-item-mobile';
import type { OpenapiClientPump } from 'src/openapi/pump';
import { useSuiNSName } from '@hooks/use-sui-ns-name';
import Buy from '@components/buy';

const Ranking: FC = () => {
  const { width } = useWindowSize();
  const marketCap = usePumpRankingMarketCap();

  const topData = marketCap.marketCap?.slice(0, 3);
  const finalMarketCap = marketCap.marketCap?.slice(3);
  return (
    <>
      <Top />
      <div className={styles.wrapper}>
        <div className={styles.banner}>
          <div className={styles.banner_text}>
            <div className={styles.banner_text1}>Leaderboard</div>
            <div className={styles.banner_text2}>Top racing driver</div>
          </div>
          <div className={styles.banner_img}>
            <Lottie
              animationData={bannerLeaderboardJSON}
              autoplay
              loop={false}
              className={styles.lottie}
            />
          </div>
        </div>

        <div className={styles.content}>
          {topData && (
            <div className={styles.top}>
              {topData.map((item, index) => (
                <ListTop item={item} index={index} key={item.token_address} />
              ))}
            </div>
          )}

          {width < 640 ? (
            <div>
              {marketCap.loading && (
                <div className={styles.loading}>
                  <Loading />
                </div>
              )}
              {finalMarketCap?.map((item, index) => (
                <PumpItemMobile item={item} index={index + 3} key={index} />
              ))}
            </div>
          ) : (
            <div>
              <div className={styles.items}>
                <ul className={`${styles.th}`}>
                  <li>Name (Symbol)</li>
                  <li>Created by</li>
                  <li className={styles.two}>Market Cap</li>
                  <li className={styles.two}>Increase</li>
                </ul>
                {marketCap.loading && (
                  <div className={styles.loading}>
                    <Loading />
                  </div>
                )}
                {finalMarketCap?.map((item, index) => {
                  return <List item={item} index={index} key={item.token_address} />;
                })}
              </div>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

const ListTop: FC<{
  item: OpenapiClientPump.GetRankingsMarketCapResponse[number];
  index: number;
}> = ({ item, index }) => {
  const navigate = useNavigate();
  const suiNames = useSuiNSName(item.created_by);
  return (
    <div className={styles.top_list} onClick={() => navigate(`/fun/${item.token_address}`)}>
      <div className={styles.top_data}>
        <div>
          <div className={styles.token}>
            <img src={rankingIcons[index]} className={styles.ranking_img} />
            <div className={styles.name_symbol}>
              <p>{item.token_metadata.name} </p>
              <p>${item.token_metadata.symbol}</p>
            </div>
          </div>
          <div className={styles.market_cap}>Market Cap:</div>
          <div className={styles.market_cap_value}>
            <div>${formatDigitUnit(item.market_cap_usd, 2)}</div>
            <div className={item.change_24h >= 0 ? '' : styles.neg}>
              {item.change_24h >= 0 ? '+' : ''}
              {Number(item.change_24h.toFixed(2))}%
            </div>
          </div>
        </div>
        <img src={item.token_metadata.iconUrl} className={styles.icon_url} />
      </div>
      <div className={styles.top_bottom}>
        <div className={styles.top_bottom_by}>by</div>
        <div className={styles.create}>
          <Avatar address={item.created_by} className={styles.create_icon} />
          <div className={styles.create_con}>
            <div className={styles.create_sender}>
              {suiNames || item.created_by.slice(item.created_by.length - 8)}
            </div>
            <div className={styles.create_time}>{timeDifference(item.deployed_at, 1)} ago</div>
          </div>
        </div>
        <Buy
          token_address={item.token_address}
          name={item.token_metadata.name}
          symbol={item.token_metadata.symbol}
          iconUrl={item.token_metadata.iconUrl}
        />
      </div>
    </div>
  );
};

const List: FC<{
  item: OpenapiClientPump.GetRankingsMarketCapResponse[number];
  index: number;
}> = ({ item, index }) => {
  const navigate = useNavigate();
  const suiNames = useSuiNSName(item.created_by);
  return (
    <ul
      className={`${styles.td}`}
      key={item.token_address}
      onClick={() => navigate(`/fun/${item.token_address}`)}
    >
      <li>
        <div className={`${styles.ranking}`}>
          <span>{index + 4}</span>
          <img src={item.token_metadata.iconUrl} />
          <div className={styles.name_symbol}>
            <p>{item.token_metadata.name} </p>
            <p>${item.token_metadata.symbol}</p>
          </div>
        </div>
      </li>
      <li>
        <div className={styles.create}>
          <Avatar address={item.created_by} className={styles.create_icon} />
          <div className={styles.create_con}>
            <div className={styles.create_sender}>
              {suiNames || item.created_by.slice(item.created_by.length - 8)}
            </div>
            <div className={styles.create_time}>{timeDifference(item.deployed_at, 1)} ago</div>
          </div>
        </div>
      </li>
      <li>
        <div className={styles.market_value}>${formatDigitUnit(item.market_cap_usd, 2)}</div>
        <div className={`${styles.market_change} ${item.change_24h >= 0 ? '' : styles.neg}`}>
          {item.change_24h >= 0 ? '+' : ''}
          {Number(item.change_24h.toFixed(2))}%
        </div>
      </li>
      <li className={styles.changes}>
        <Buy
          token_address={item.token_address}
          name={item.token_metadata.name}
          symbol={item.token_metadata.symbol}
          iconUrl={item.token_metadata.iconUrl}
        />
      </li>
    </ul>
  );
};

export default Ranking;
